import { CancelToken } from 'axios';
import mapValues from 'lodash/mapValues';
import merge from 'lodash-es/merge';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { LibraryAPI } from '@/apis';
import { handleCancelError, isCancelError, refreshAbortController } from '@/apis/axios.utils';
import * as CreatorsAPI from '@/apis/creators';
import { getPredictions } from '@/apis/library';
import { useIGCMStatus } from '@/app/creators/composables/useIGCMStatus';
import { creatorSource, creatorSources } from '@/app/creators/constants';
import { sanitizeStoriesFromDMs } from '@/app/creators/utils';
import { env } from '@/env';
import { useIdentityStore } from '@/stores/identity';
import { browserStorageGetItem, browserStorageSetItem } from '@/utils/browserStorage';

const RECENT_LOCATIONS_KEY = 'creator-discovery-recent-locations';
export const MAX_RECENT_LOCATIONS = 3;

export const useCreatorsStore = defineStore('creators', () => {
  const { setIGCStatusFromAxiosCall } = useIGCMStatus();

  const creators = ref([]);
  const creatorsCount = ref();
  const account = ref();
  const tikTokAccount = ref();
  const instagramAccount = ref();
  const instagramAccountPosts = ref([]);
  const tikTokAccountPosts = ref([]);
  const tikTokRIQPosts = ref([]);
  const accountPosts = ref([]);
  const brandRelationshipTags = ref([]);
  const creatorInformationRequest = ref([]);
  const accountMetricsBySource = ref(mapValues(creatorSource, () => null));
  const globalCustomFields = ref();
  // new store variable we'll be able to check from inside the component (like .relationships in the IG relationship store)
  const createdRelationships = ref();

  // Creator Profile Drawer Related State
  const creator = ref({});
  const editCreator = ref(false);
  const onReloadCreatorProfileCallback = ref();
  const instagramInvitationEmailSent = ref(false);
  const creatorProfileDetailsFormValidation = ref(false);
  const unsavedCreatorData = ref();
  const creatorDrawerId = ref(null);
  // To track the creator ID when leaving for settings
  const creatorProfileCustomFieldsReturnId = ref(null);
  // Information Request Form state
  const informationRequestData = ref({});
  const pendingSubmitIRF = ref(false);
  // Creator Profile Custom Fields Settings
  const customFieldsInSettings = ref([]);

  const multiBrandCreators = ref({});
  const multiBrandRelationshipTags = ref({});

  const isSearchingTikTokDiscovery = ref(false);

  const brandedContent = ref([]);
  const brandedContentPredictions = ref({});
  const pastBrandPartnerships = ref([]);

  const brandedContentWithPredictions = computed(() =>
    brandedContent.value.map((post) => {
      const prediction = brandedContentPredictions.value[post.id];
      return prediction !== undefined
        ? { ...post, predictions: { engagement: prediction ?? 'processing' } }
        : post;
    }),
  );

  const pendingCounts = ref({
    account: 0,
    accountPosts: 0,
    tikTokAccountPosts: 0,
    accountMetricsBySource: 0,
    accountAvgWeeklyPosts: 0,
    creator: 0,
    creators: 0,
    creatorsCount: 0,
    creatorDetails: 0,
    deleteRelationship: 0,
    addBrandRelationshipTags: 0,
    deleteBrandRelationshipTags: 0,
    brandRelationshipTags: 0,
    relationships: 0,
    createInformationRequest: 0,
    removeRelationshipAccount: 0,
    globalCustomFields: 0,
    brandedContent: 0,
    pastBrandPartnerships: 0,
  });

  const creatorsCancelToken = ref(null);
  const accountCancelToken = ref({
    instagram: null,
    tiktok: null,
  });

  const paging = ref({
    nextCursor: null,
    lastRequestBody: null,
  });

  const postsPaging = ref({
    accountPostsNextUrl: null,
    tikTokPostsNextCursor: null,
  });

  const abortControllers = {
    getAccountPosts: ref(null),
    fetchTikTokAccountPosts: ref(null),
    fetchAccountMetrics: ref(null),
    fetchAccountAvgWeeklyPosts: ref(null),
    fetchBrandedContent: ref(null),
  };

  const recentLocations = ref(JSON.parse(browserStorageGetItem(RECENT_LOCATIONS_KEY) || '[]'));

  const error = ref({
    tikTokAccountPosts: null,
    fetchYourCreators: null,
    fetchBrandedContent: null,
    fetchPastBrandPartnerships: null,
  });

  const hasAccountsNextPage = computed(
    () => !!paging.value.nextCursor && !!paging.value.lastRequestBody,
  );

  const accountExistsBySource = computed(() => {
    const accounts = creator.value?.accounts || {};
    return Object.fromEntries(
      creatorSources.map((source) => [source, !!accounts[source.toLowerCase()]]),
    );
  });

  const isMultiChannel = computed(
    () => Object.values(accountExistsBySource.value).filter(Boolean)?.length > 1,
  );

  const pending = computed(() =>
    Object.fromEntries(Object.entries(pendingCounts.value).map(([k, v]) => [k, !!v])),
  );

  function addRecentLocations(newLocations) {
    recentLocations.value = [
      ...[...newLocations].reverse(),
      ...recentLocations.value.filter((location) => !newLocations.includes(location)),
    ].slice(0, MAX_RECENT_LOCATIONS);
    browserStorageSetItem(RECENT_LOCATIONS_KEY, JSON.stringify(recentLocations.value));
  }

  function getCreatorProfilePhoto() {
    if (creator?.value?.profilePhotoUrl) return creator.value.profilePhotoUrl;
    return null;
  }

  async function fetchAccount({ source, sourceCreatorId, handle, brandId }, axiosConfig = {}) {
    pendingCounts.value.account += 1;
    try {
      const response = await setIGCStatusFromAxiosCall(
        CreatorsAPI.fetchAccount({ source, sourceCreatorId, handle, brandId }, axiosConfig),
      );
      account.value = response.data.data;
      return response.data.data;
    } catch (err) {
      if (err?.response?.status !== 404) throw err;
      return null;
    } finally {
      pendingCounts.value.account -= 1;
    }
  }

  async function fetchTikTokAccount({ brandId, handle }) {
    accountCancelToken.value.tiktok?.cancel();
    accountCancelToken.value.tiktok = CancelToken.source();
    try {
      tikTokAccount.value = await fetchAccount(
        { brandId, handle, source: creatorSource.TIKTOK },
        { cancelToken: accountCancelToken.value.tiktok.token },
      );
    } catch (e) {
      if (!isCancelError(e)) throw e;
    }
  }

  async function clearTikTokAccount() {
    accountCancelToken.value.tiktok?.cancel();
    tikTokAccount.value = null;
    account.value = null;
  }

  async function fetchInstagramAccount({ handle, brandId, sourceCreatorId }) {
    accountCancelToken.value.instagram?.cancel();
    accountCancelToken.value.instagram = CancelToken.source();
    try {
      instagramAccount.value = await fetchAccount(
        {
          handle,
          brandId,
          sourceCreatorId,
          source: creatorSource.INSTAGRAM,
        },
        { cancelToken: accountCancelToken.value.instagram.token },
      );
    } catch (e) {
      if (!isCancelError(e)) throw e;
    }
  }

  async function clearInstagramAccount() {
    accountCancelToken.value.instagram?.cancel();
    instagramAccount.value = null;
    account.value = null;
  }

  async function fetchMultiBrandCreators(brandIds) {
    try {
      pendingCounts.value.creators += 1;
      let nextCursor;
      const creatorsData = [];

      const searchRelationshipsRecursive = async () => {
        const response = await CreatorsAPI.searchRelationships({
          brandIds,
          limit: 60,
          afterCursor: nextCursor,
        });
        const data = response.data?.data || response.data || {};

        creatorsData.push(data);

        if (response.data.paging.nextCursor === nextCursor) return;

        nextCursor = response.data.paging.nextCursor;
        if (nextCursor) {
          await searchRelationshipsRecursive();
        }
      };

      await searchRelationshipsRecursive();
      multiBrandCreators.value = creatorsData.reduce((acc, curr) => merge(acc, curr), {});
    } finally {
      pendingCounts.value.creators = false;
    }
  }

  async function fetchAllBrandCreators(brandId = null) {
    let targetBrandId = brandId;
    if (!brandId) {
      const identityStore = useIdentityStore();
      targetBrandId = identityStore.currentBrand.id;
    }

    try {
      pendingCounts.value.creators += 1;
      let nextCursor;
      const data = [];

      const fetchBrandCreatorsRecursive = async () => {
        const response = await CreatorsAPI.fetchBrandCreators({
          afterCursor: nextCursor,
          brandId: targetBrandId,
        });
        data.push(...response.data.data);
        nextCursor = response.data.paging.nextCursor;

        if (nextCursor) {
          await fetchBrandCreatorsRecursive();
        }
      };

      await fetchBrandCreatorsRecursive();
      creators.value = data;
    } finally {
      pendingCounts.value.creators = false;
    }
    return creators.value;
  }

  async function fetchBrandCreators({ creatorIds }) {
    const identityStore = useIdentityStore();
    const brandId = identityStore.currentBrand.id;

    pendingCounts.value.creators += 1;
    try {
      const response = await CreatorsAPI.fetchBrandCreators({
        brandId,
        creatorIds,
      });
      creators.value = response.data.data;
    } finally {
      pendingCounts.value.creators -= 1;
    }
    return creators.value;
  }

  async function removeRelationshipAccount({ creatorId, source }) {
    const identityStore = useIdentityStore();
    const brandId = identityStore.currentBrand.id;

    pendingCounts.value.removeRelationshipAccount += 1;
    try {
      const response = await CreatorsAPI.removeRelationshipAccount({
        brandId,
        creatorId,
        source,
      });
      creator.value = response.data.data[0];
    } finally {
      pendingCounts.value.removeRelationshipAccount -= 1;
    }
  }

  async function getAccountPosts({
    brandId,
    limit,
    offset,
    sortFields,
    filters,
    includeCreator,
    campaignCreatorFiltersOptions,
  }) {
    pendingCounts.value.accountPosts += 1;

    try {
      const signal = refreshAbortController(abortControllers.getAccountPosts);

      const response = await LibraryAPI.getMediaListV2(
        {
          brandId,
          limit,
          offset,
          sortFields,
          filters: { ...filters, includeDisconnectedStoryUgc: true },
          includeCreator,
          url: postsPaging?.value?.accountPostsNextUrl,
          campaignCreatorFiltersOptions,
        },
        { signal },
      );
      const payload = response.data;

      if (payload.data) {
        // we only set includeCreator if the source is INSTAGRAM, so will add it to instagramAccountPosts
        if (includeCreator) {
          instagramAccountPosts.value = sanitizeStoriesFromDMs([
            ...instagramAccountPosts.value,
            ...payload.data,
          ]);
        } else tikTokRIQPosts.value = [...tikTokRIQPosts.value, ...payload.data];

        accountPosts.value = [...accountPosts.value, ...payload.data];
        postsPaging.value.accountPostsNextUrl = payload?.paging?.next;
      }

      return payload;
    } catch (e) {
      return handleCancelError(e);
    } finally {
      pendingCounts.value.accountPosts -= 1;
    }
  }

  function clearCreator() {
    pendingCounts.value.creator = 0;
    pendingCounts.value.creatorDetails = 0;
    editCreator.value = false;
    creator.value = undefined;
    instagramInvitationEmailSent.value = false;
  }

  async function fetchCreator({
    creatorId,
    postsPublishedStartDate,
    postsPublishedEndDate,
    instagramPostType,
    youtubePostType,
    isFetchingMetrics,
  }) {
    const identityStore = useIdentityStore();
    const brandId = identityStore.currentBrand.id;

    // Since we're also using this wrapper for fetching RIQ posts metrics, we don't want creator profile to be skeleton loading
    if (!isFetchingMetrics) pendingCounts.value.creator += 1;
    try {
      const response = await CreatorsAPI.fetchBrandCreators({
        brandId,
        creatorIds: [creatorId],
        postsPublishedEndDate,
        postsPublishedStartDate,
        instagramPostType,
        youtubePostType,
      });
      if (response.data.data.length > 0) {
        const newData = response.data.data[0];
        creator.value = {
          ...newData,
          orders: creator.value?.orders,
        };
      }
    } finally {
      if (!isFetchingMetrics) pendingCounts.value.creator -= 1;
    }
  }

  async function fetchSingleCreator({ creatorId }) {
    const identityStore = useIdentityStore();
    const brandId = identityStore.currentBrand.id;

    pendingCounts.value.creator += 1;
    try {
      const response = await CreatorsAPI.fetchBrandCreator({
        brandId,
        creatorId,
      });
      if (response.data.data) {
        creator.value = response.data.data;
      }
    } finally {
      pendingCounts.value.creator -= 1;
    }
  }

  async function fetchCreatorByHandle({ handle, channel, isFetchingMetrics, shouldFetchDetails }) {
    const identityStore = useIdentityStore();
    const brandId = identityStore.currentBrand.id;

    if (!isFetchingMetrics) pendingCounts.value.creator += 1;

    try {
      let response = await CreatorsAPI.searchCreators({
        brandId,
        limit: 1,
        lookupName: handle,
        restrictToPlatforms: [channel],
      });

      // can only get custom creator details (about section etc.) if you specifically search by ID
      if (shouldFetchDetails && response.data.data.length > 0) {
        const basicCreator = response.data.data[0];
        response = await CreatorsAPI.fetchBrandCreators({
          brandId,
          creatorIds: [basicCreator.creatorId],
        });
      }
      if (response.data.data.length > 0) creator.value = response.data.data[0];
    } finally {
      if (!isFetchingMetrics) pendingCounts.value.creator -= 1;
    }
  }

  async function updateCreatorDetails({ creatorId, creatorDetails }) {
    const identityStore = useIdentityStore();
    const brandId = identityStore.currentBrand.id;

    pendingCounts.value.creatorDetails += 1;
    try {
      await CreatorsAPI.updateCreatorDetails({
        brandId,
        creatorId,
        creatorDetails,
      });
      const response = await CreatorsAPI.fetchBrandCreators({
        brandId,
        creatorIds: [creatorId],
      });
      creator.value = response.data.data[0];
    } catch (e) {
      if (!isCancelError(e)) throw e;
    } finally {
      pendingCounts.value.creatorDetails -= 1;
    }
  }

  function clearAccountsPosts() {
    instagramAccountPosts.value = [];
    tikTokRIQPosts.value = [];
    accountPosts.value = [];
    postsPaging.value.accountPostsNextUrl = null;
  }

  function clearAccount() {
    account.value = null;
  }

  async function fetchBrandRelationshipTags() {
    const identityStore = useIdentityStore();
    const brandId = identityStore.currentBrand.id;

    try {
      pendingCounts.value.brandRelationshipTags += 1;

      let nextUrl;
      const data = [];

      const fetchBrandRelationshipTagsRecursive = async () => {
        const response = await CreatorsAPI.fetchBrandRelationshipTags({ url: nextUrl, brandId });
        data.push(...response.data.data);
        nextUrl = response.data.paging.next;

        if (nextUrl) {
          await fetchBrandRelationshipTagsRecursive();
        }
      };

      await fetchBrandRelationshipTagsRecursive();

      brandRelationshipTags.value = data;
    } finally {
      pendingCounts.value.brandRelationshipTags -= 1;
    }
  }

  async function fetchInformationRequest(token) {
    try {
      creatorInformationRequest.value = await CreatorsAPI.fetchInformationRequest(token);
      return creatorInformationRequest.value;
    } catch (e) {
      return e.response;
    }
  }

  async function submitInformationRequest({ token, creatorData }) {
    try {
      pendingSubmitIRF.value = true;
      const customFields = creatorData?.customFields
        ? Object.entries(creatorData.customFields).map(([key, value]) => ({
            id: key.split('-').pop(),
            value,
          }))
        : [];
      return await CreatorsAPI.submitInformationRequest({
        token,
        creatorData: { ...creatorData, customFields },
      });
    } finally {
      pendingSubmitIRF.value = false;
    }
  }

  async function deleteRelationship({ creatorId }) {
    const identityStore = useIdentityStore();
    const brandId = identityStore.currentBrand.id;
    const creatorIds = [creatorId];
    pendingCounts.value.deleteRelationship += 1;
    try {
      await CreatorsAPI.bulkDeleteRelationships({
        brandId,
        creatorIds,
      });

      creators.value = creators.value.filter((t) => t.creatorId !== parseInt(creatorId, 10));
    } catch (e) {
      if (!isCancelError(e)) throw e;
    } finally {
      pendingCounts.value.deleteRelationship -= 1;
    }
  }

  async function bulkDeleteRelationships({ creatorIds }) {
    const identityStore = useIdentityStore();
    const brandId = identityStore.currentBrand.id;
    pendingCounts.value.deleteRelationship += 1;
    try {
      await CreatorsAPI.bulkDeleteRelationships({
        brandId,
        creatorIds,
      });

      creators.value = creators.value.filter((t) => !creatorIds.includes(t.creatorId));
    } catch (e) {
      if (!isCancelError(e)) throw e;
    } finally {
      pendingCounts.value.deleteRelationship -= 1;
    }
  }

  async function createRelationships({ creatorAccounts }) {
    const identityStore = useIdentityStore();
    const brandId = identityStore.currentBrand.id;

    // reset the variable to null before we try this- otherwise it might still have results stored from a previous attempt
    createdRelationships.value = null;
    pendingCounts.value.relationships += 1;
    try {
      const creatorResponse = await CreatorsAPI.findOrCreateCreators({
        brandId,
        creators: creatorAccounts,
      });
      const creatorIds = creatorResponse.data.data.map(({ id }) => id);

      const relationshipsResponse = await CreatorsAPI.createRelationships({
        brandId,
        creatorIds,
      });
      // cool, we made it this far and creator backend responded with some relationship data.
      // update the store variable to the relationship we just made.
      createdRelationships.value = relationshipsResponse.data.data;
      // now that the value has changed, we'll be able to see + use it in the UserSection component
      const existingRelationships = relationshipsResponse.data.data.map(
        ({ alreadyExists }) => alreadyExists,
      );
      return { creatorIds, existingRelationships };
    } catch (e) {
      if (!isCancelError(e)) throw e;
      return null;
    } finally {
      pendingCounts.value.relationships -= 1;
    }
  }

  // Your Creators Related State
  const creatorsPaging = ref({
    nextCursor: null,
    requestBody: null,
  });

  async function fetchYourCreators(
    {
      limit,
      sort,
      lookupName,
      restrictToPlatforms,
      postsPublishedStartDate,
      postsPublishedEndDate,
      tagFilterType,
      tags,
      creatorLocations,
      hasMentionedBrand,
      creatorIds,
      instagramPostType,
      instagramConnectionStatus,
      instagramFollowersGreaterThan,
      instagramFollowersLessThan,
      instagramInterests,
      instagramIsBusiness,
      instagramHandle,
      tiktokConnectionStatus,
      tiktokFollowersGreaterThan,
      tiktokFollowersLessThan,
      tiktokMinEngagementRate,
      tiktokMaxEngagementRate,
      tiktokTTCMStatus,
      tiktokMinMedianViews,
      tiktokMaxMedianViews,
      tiktokAudienceRegions,
      tiktokAudienceGender,
      tiktokAudienceAge,
      tiktokMinCreatorRate,
      tiktokMaxCreatorRate,
      tiktokCreatorCurrency,
      tiktokHandle,
      tiktokOrganicInterests,
      tiktokPaidInterests,
      youtubePostType,
      youtubeFollowersGreaterThan,
      youtubeFollowersLessThan,
      youtubeVideoCountGreaterThan,
      youtubeVideoCountLessThan,
      youtubeViewsGreaterThan,
      youtubeViewsLessThan,
      competitiveMentions,
    },
    refresh = true,
    returnResultsOnly = false,
  ) {
    // Reset error state
    error.value.fetchYourCreators = null;

    const identityStore = useIdentityStore();
    const brandId = identityStore.currentBrand.id;
    if (refresh && !returnResultsOnly) {
      creators.value = [];
      creatorsPaging.value = {
        nextCursor: null,
        requestBody: null,
      };
    }

    if (!returnResultsOnly) pendingCounts.value.creators += 1;
    creatorsPaging.value.requestBody = {
      limit,
      sort,
      lookupName,
      restrictToPlatforms,
      postsPublishedStartDate,
      postsPublishedEndDate,
      tagFilterType,
      tags,
      creatorLocations,
      hasMentionedBrand,
      creatorIds,
      instagramPostType,
      instagramConnectionStatus,
      instagramFollowersGreaterThan,
      instagramFollowersLessThan,
      instagramInterests,
      instagramIsBusiness,
      instagramHandle,
      tiktokConnectionStatus,
      tiktokFollowersGreaterThan,
      tiktokFollowersLessThan,
      tiktokMinEngagementRate,
      tiktokMaxEngagementRate,
      tiktokTTCMStatus,
      tiktokMinMedianViews,
      tiktokMaxMedianViews,
      tiktokAudienceRegions,
      tiktokAudienceGender,
      tiktokAudienceAge,
      tiktokMinCreatorRate,
      tiktokMaxCreatorRate,
      tiktokCreatorCurrency,
      tiktokHandle,
      tiktokOrganicInterests,
      tiktokPaidInterests,
      youtubePostType,
      youtubeFollowersGreaterThan,
      youtubeFollowersLessThan,
      youtubeVideoCountGreaterThan,
      youtubeVideoCountLessThan,
      youtubeViewsGreaterThan,
      youtubeViewsLessThan,
      competitiveMentions,
    };
    creatorsCancelToken.value?.cancel();
    creatorsCancelToken.value = CancelToken.source();
    try {
      const response = await CreatorsAPI.searchCreators(
        {
          brandId,
          ...creatorsPaging.value.requestBody,
        },
        {
          cancelToken: creatorsCancelToken.value.token,
        },
      );

      const data = response.data.data;
      const nextCursor = response.data.paging.nextCursor;

      if (returnResultsOnly) {
        return { data, nextCursor };
      }

      creators.value = data;
      creatorsPaging.value.nextCursor = nextCursor;
    } catch (e) {
      if (!isCancelError(e)) {
        error.value.fetchYourCreators = e;
        throw e;
      }
    } finally {
      if (!returnResultsOnly) pendingCounts.value.creators -= 1;
    }
    return null;
  }

  async function fetchYourCreatorsNextPage() {
    if (!creatorsPaging.value.nextCursor) return;
    const identityStore = useIdentityStore();
    const brandId = identityStore.currentBrand.id;
    pendingCounts.value.creators += 1;
    creatorsCancelToken.value = CancelToken.source();
    try {
      const response = await CreatorsAPI.searchCreators(
        {
          brandId,
          ...creatorsPaging.value.requestBody,
          afterCursor: creatorsPaging.value.nextCursor,
        },
        { cancelToken: creatorsCancelToken.value.token },
      );
      creators.value = [...creators.value, ...response.data.data];
      creatorsPaging.value.nextCursor = response.data.paging.nextCursor;
    } catch (e) {
      if (!isCancelError(e)) throw e;
    } finally {
      pendingCounts.value.creators -= 1;
    }
  }

  async function fetchTikTokAccountPosts({
    brandId,
    source,
    handle,
    includeAuthorized,
    cache,
    limit,
    includeAllMetrics = false,
  }) {
    pendingCounts.value.tikTokAccountPosts += 1;
    error.value.tikTokAccountPosts = null;

    try {
      const signal = refreshAbortController(abortControllers.fetchTikTokAccountPosts);

      const response = await CreatorsAPI.fetchAccountPosts(
        {
          brandId,
          source,
          handle,
          includeAuthorized,
          cache,
          includeAllMetrics,
          afterCursor: postsPaging.value.tikTokPostsNextCursor,
          limit,
        },
        { signal },
      );
      tikTokAccountPosts.value = [...tikTokAccountPosts.value, ...response.data.data];
      postsPaging.value.tikTokPostsNextCursor = response.data.paging.nextCursor;
    } catch (e) {
      if (!isCancelError(e)) {
        error.value.tikTokAccountPosts = e.response;
      }
      handleCancelError(e);
    } finally {
      pendingCounts.value.tikTokAccountPosts -= 1;
    }
  }

  function clearTikTokAccountPosts() {
    postsPaging.value.tikTokPostsNextCursor = null;
    pendingCounts.value.tikTokAccountPosts = 0;
    tikTokAccountPosts.value = [];
    error.value.tikTokAccountPosts = null;
  }

  async function fetchAvgWeeklyPosts({ source, handle, brandId }) {
    pendingCounts.value.accountAvgWeeklyPosts += 1;

    try {
      const signal = refreshAbortController(abortControllers.fetchAccountAvgWeeklyPosts);
      const response = await CreatorsAPI.fetchAccountAvgWeeklyPosts(
        { source, handle, brandId },
        { signal },
      );

      accountMetricsBySource.value[source] = {
        ...(accountMetricsBySource.value[source] || {}),
        avgWeeklyPosts: response.data.data.avgWeeklyPosts,
      };
    } catch (err) {
      if (err?.response?.status !== 404) {
        handleCancelError(err);
      }
    } finally {
      pendingCounts.value.accountAvgWeeklyPosts -= 1;
    }
  }

  async function fetchAccountMetrics({ source, handle, brandId }) {
    pendingCounts.value.accountMetricsBySource += 1;
    try {
      const signal = refreshAbortController(abortControllers.fetchAccountMetrics);
      const response = await CreatorsAPI.fetchAccountMetrics(
        { source, handle, brandId },
        { signal },
      );
      accountMetricsBySource.value[source] = {
        ...(accountMetricsBySource.value[source] || {}),
        ...response.data.data,
      };
    } catch (err) {
      if (err?.response?.status === 404) {
        accountMetricsBySource.value[source] = null;
      } else {
        handleCancelError(err);
      }
    } finally {
      pendingCounts.value.accountMetricsBySource -= 1;
    }
  }

  function clearAccountMetrics() {
    accountMetricsBySource.value = mapValues(creatorSource, () => null);
  }

  async function updateInvitationStatus({
    source,
    sourceCreatorId,
    invitedByBrandId,
    emailContent,
    invitationStatus = undefined,
  }) {
    try {
      const res = await CreatorsAPI.updateInvitationStatus({
        source,
        sourceCreatorId,
        invitedByBrandId,
        invitationStatus,
        emailContent,
      });
      return res;
    } catch (e) {
      if (!isCancelError(e)) throw e;
      return e.response;
    }
  }

  async function createInformationRequest() {
    pendingCounts.value.createInformationRequest += 1;

    const identityStore = useIdentityStore();
    const brandId = identityStore.currentBrand.id;
    try {
      const res = await CreatorsAPI.createInformationRequest({
        brandId,
        creatorId: creator.value.creatorId,
      });
      const token = res.data.token;

      const url = new URL(`${env.dashwebUrl}/information-request-form`);
      url.searchParams.set('t', token);

      informationRequestData.value.url = url.href;
      informationRequestData.value.token = token;
      informationRequestData.value.id = res.data.id;
    } catch (e) {
      if (!isCancelError(e)) throw e;
    } finally {
      pendingCounts.value.createInformationRequest -= 1;
    }
  }

  async function addRelationshipAccount({ creatorId, handle, source }) {
    const identityStore = useIdentityStore();
    const brandId = identityStore.currentBrand.id;

    try {
      const response = await CreatorsAPI.addRelationshipAccount({
        brandId,
        creatorId,
        handle,
        source,
      });
      creator.value = response.data.data[0];
      creators.value = creators.value.filter((t) => {
        const a = t.accounts?.[source.toLowerCase()];
        return !(a && a.handle === handle);
      });
    } catch (e) {
      if (!isCancelError(e)) throw e;
    }
  }

  function onReloadCreatorProfile(callback) {
    if (typeof callback === 'function') {
      onReloadCreatorProfileCallback.value = callback;
    }
  }

  function triggerCreatorProfileReload() {
    if (onReloadCreatorProfileCallback.value) {
      onReloadCreatorProfileCallback.value();
    }
  }

  function setInstagramInvitationEmailSent(value) {
    instagramInvitationEmailSent.value = value;
  }

  async function fetchGlobalCustomFields() {
    const identityStore = useIdentityStore();
    const brandId = identityStore.currentBrand.id;

    pendingCounts.value.globalCustomFields += 1;
    try {
      const response = await CreatorsAPI.fetchCustomFields({ brandId });
      globalCustomFields.value = response.data.data;
    } finally {
      pendingCounts.value.globalCustomFields -= 1;
    }

    return globalCustomFields.value;
  }

  async function fetchCustomFields({ brandId, force = false }) {
    if (customFieldsInSettings.value.length > 0 && !force) {
      return;
    }

    try {
      const response = await CreatorsAPI.fetchCustomFields({ brandId });

      if (response.data.data.length === 0) {
        customFieldsInSettings.value = [{ name: '', showInRequestForm: true }];
      } else {
        customFieldsInSettings.value = response.data.data;
      }
    } catch (e) {
      if (e?.response?.status === 404) {
        customFieldsInSettings.value = [];
      } else {
        throw e;
      }
    }
  }

  async function saveCustomFields({ brandId, fields }) {
    try {
      const updatePromises = fields
        .filter((field) => field.id)
        .map((field) =>
          CreatorsAPI.updateCustomField({
            brandId,
            fieldId: field.id,
            name: field.name,
            showInRequestForm: field.showInRequestForm,
          }),
        );

      const newFields = fields.filter((field) => !field.id);
      let createPromise = null;
      if (newFields.length > 0) {
        createPromise = CreatorsAPI.createCustomFields({
          brandId,
          customFields: newFields,
        });
      }

      await Promise.all([...updatePromises, createPromise].filter(Boolean));
      await fetchCustomFields({ brandId, force: true });
    } catch (e) {
      if (!isCancelError(e)) throw e;
    }
  }

  async function deleteCustomField({ brandId, fieldId }) {
    try {
      await CreatorsAPI.deleteCustomField({ brandId, fieldId });
      customFieldsInSettings.value = customFieldsInSettings.value.filter((f) => f.id !== fieldId);
    } catch (e) {
      if (!isCancelError(e)) throw e;
    }
  }

  async function fetchRelationshipTagsMultiBrand({ brandIds }) {
    if (!brandIds || !Array.isArray(brandIds) || brandIds.length === 0) {
      return;
    }
    let nextUrl;
    let tagsData = {};
    const searchRelationshipTagsRecursive = async () => {
      const response = await CreatorsAPI.getRelationshipTags({ brandIds, nextUrl });

      const data = response.data?.data || response.data || {};
      tagsData = merge(tagsData, data);
      nextUrl = response.data?.paging?.next;

      if (nextUrl) {
        // TODO: Uncomment when pagination url bug is fixed. See sc-166476 or PR #20589
        // await searchRelationshipTagsRecursive();
      }
    };

    await searchRelationshipTagsRecursive();

    multiBrandRelationshipTags.value = tagsData;
  }

  async function fetchCreatorsCount(brandIds = null) {
    const identityStore = useIdentityStore();
    let brands = brandIds;
    if (!brands) {
      const brandId = identityStore.currentBrand.id;
      brands = [brandId];
    }

    try {
      pendingCounts.value.creatorsCount += 1;

      if (brands.length > 1) {
        const response = await CreatorsAPI.searchRelationshipsMultiBrand({
          brandIds: brands,
          limit: 1,
        });
        creatorsCount.value = response.data.paging.size;
      }

      const brandId = brands[0];
      const response = await CreatorsAPI.fetchCreatorsCount({ brandId });

      creatorsCount.value = response.data.paging.size;
    } catch (e) {
      if (!isCancelError(e)) throw e;
    } finally {
      pendingCounts.value.creatorsCount -= 1;
    }
  }

  async function fetchBrandedContent({ brandId, handle, source }) {
    brandedContent.value = [];
    brandedContentPredictions.value = {};
    error.value.fetchBrandedContent = null;
    pendingCounts.value.brandedContent += 1;
    try {
      const signal = refreshAbortController(abortControllers.fetchBrandedContent);
      const response = await setIGCStatusFromAxiosCall(
        CreatorsAPI.fetchBrandedContent({ brandId, handle, source }, { signal }),
      );
      brandedContent.value = response.data.data;
    } catch (e) {
      if (!isCancelError(e)) {
        error.value.fetchBrandedContent = e;
        throw e;
      }
    } finally {
      pendingCounts.value.brandedContent -= 1;
    }
  }

  function getPostThumbnailUrl(post) {
    return post.video?.thumbnails?.smallSquare?.url ?? post.image?.sizes?.smallSquare?.url ?? null;
  }

  async function fetchBrandedContentPredictions({ brandId, posts }) {
    const postsWithImages = posts.filter((post) => getPostThumbnailUrl(post));
    if (postsWithImages.length === 0) return;

    // Initialize predictions as processing
    postsWithImages.forEach((post) => {
      brandedContentPredictions.value[post.id] = 'processing';
    });

    await Promise.all(
      postsWithImages.map(async (post) => {
        try {
          const response = await getPredictions({
            brandId,
            files: [{ id: post.id, url: getPostThumbnailUrl(post) }],
          });
          const newPredictions = response?.data ?? {};
          brandedContentPredictions.value = {
            ...brandedContentPredictions.value,
            ...newPredictions,
          };
        } catch (e) {
          // Clear processing state on error
          delete brandedContentPredictions.value[post.id];
          throw e;
        }
      }),
    );
  }

  async function fetchPastBrandPartnerships({ brandId, handle, source }) {
    pendingCounts.value.pastBrandPartnerships += 1;
    error.value.fetchPastBrandPartnerships = null;
    try {
      const response = await setIGCStatusFromAxiosCall(
        CreatorsAPI.fetchPastBrandPartnerships({ brandId, handle, source }),
      );
      pastBrandPartnerships.value = response.data?.data?.pastBrandPartnerships;
    } catch (e) {
      if (!isCancelError(e)) {
        error.value.fetchPastBrandPartnerships = e;
        throw e;
      }
    } finally {
      pendingCounts.value.pastBrandPartnerships -= 1;
    }
  }

  return {
    account,
    tikTokAccount,
    instagramAccount,
    pending,
    error,
    hasAccountsNextPage,
    clearAccount,
    fetchAccount,
    fetchTikTokAccount,
    fetchInstagramAccount,
    clearTikTokAccount,
    clearInstagramAccount,
    recentLocations,
    addRecentLocations,
    paging,
    creators,
    fetchAllBrandCreators,
    fetchMultiBrandCreators,
    fetchBrandCreators,
    getAccountPosts,
    instagramAccountPosts,
    brandRelationshipTags,
    fetchBrandRelationshipTags,
    fetchInformationRequest,
    submitInformationRequest,
    pendingSubmitIRF,
    creatorInformationRequest,
    informationRequestData,
    updateCreatorDetails,
    creator,
    multiBrandCreators,
    editCreator,
    fetchCreator,
    fetchCreatorsCount,
    fetchSingleCreator,
    fetchCreatorByHandle,
    clearCreator,
    clearAccountsPosts,
    deleteRelationship,
    bulkDeleteRelationships,
    creatorsPaging,
    fetchYourCreators,
    fetchYourCreatorsNextPage,
    createRelationships,
    removeRelationshipAccount,
    creatorsCount,
    tikTokAccountPosts,
    tikTokRIQPosts,
    fetchTikTokAccountPosts,
    clearTikTokAccountPosts,
    accountMetricsBySource,
    fetchAccountMetrics,
    fetchAvgWeeklyPosts,
    clearAccountMetrics,
    updateInvitationStatus,
    postsPaging,
    getCreatorProfilePhoto,
    createInformationRequest,
    onReloadCreatorProfile,
    triggerCreatorProfileReload,
    instagramInvitationEmailSent,
    setInstagramInvitationEmailSent,
    addRelationshipAccount,
    creatorProfileDetailsFormValidation,
    unsavedCreatorData,
    globalCustomFields,
    fetchGlobalCustomFields,
    customFieldsInSettings,
    fetchCustomFields,
    saveCustomFields,
    deleteCustomField,
    creatorDrawerId,
    creatorProfileCustomFieldsReturnId,
    createdRelationships,
    accountExistsBySource,
    isMultiChannel,
    accountPosts,
    multiBrandRelationshipTags,
    fetchRelationshipTagsMultiBrand,
    isSearchingTikTokDiscovery,
    brandedContent,
    brandedContentPredictions,
    brandedContentWithPredictions,
    fetchBrandedContent,
    fetchBrandedContentPredictions,
    fetchPastBrandPartnerships,
    pastBrandPartnerships,
  };
});
